import React, { useMemo, useCallback, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { ClipboardList, RefreshCw } from 'lucide-react';
import { 
  useGetConditionLookupsQuery,
  useConditionFilters,
  useConditionActions,
  ConditionDetailsModal,
  ConditionGroupsModal,
  createConditionsColumns,
  createConditionsActions,
  ConditionLookup,
  useBulkChangeStatusMutation,
} from '@entities/conditions';
import { ConditionCreateModal, ConditionEditModal } from '@entities/conditions/components/ConditionCreateEditModal';
import EntityTableSection from '@/components/section/EntityTableSection';
import type { BulkAction } from '@/components/section/EntityTableSection';
import PageHeader from '@/components/ui/PageHeader';
import SearchAndFiltersSection, { Filter } from '@/components/section/SearchAndFiltersSection';
import ErrorAlert from '@/components/ErrorAlert';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import Pagination from '@/components/ui/Pagination';
import { useGetSpecialitiesQuery } from '@entities/specialities/api';
import { useGetConditionGroupsLookupsQuery, ConditionGroupLookup } from '@entities/condition-groups/api';
import { LOOKUP_STATUS_FILTER_OPTIONS, LOOKUP_STATUS_OPTIONS, getStatusBadgeProps } from '@/utils/lookupStatus';
import type { LookupStatus } from '@/utils/lookupStatus';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { useModal } from '@/hooks/useModal';

const getConditionId = (item: ConditionLookup) => item.id;

const ConditionsPage: React.FC = () => {
  const { filters, queryParams, handleFilterChange } = useConditionFilters();
  const actions = useConditionActions();
  const { data: specialities = [] } = useGetSpecialitiesQuery();
  
  // Fetch condition groups at page level to avoid N+1 fetches in table rows
  const { data: conditionGroupsResponse } = useGetConditionGroupsLookupsQuery();
  const conditionGroups = conditionGroupsResponse?.data || [];

  // Create look-up map for O(1) access in table cells
  const conditionGroupsMap = useMemo(() => {
    return conditionGroups.reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {} as Record<number, ConditionGroupLookup>);
  }, [conditionGroups]);
  
  const {
    data: paginatedResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetConditionLookupsQuery(queryParams, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const conditionsArray = paginatedResponse?.data || [];
  const filteredConditions = conditionsArray;

  // Multi-select
  const multiSelect = useMultiSelect(conditionsArray);
  const [bulkChangeStatus, { isLoading: isBulkUpdating }] = useBulkChangeStatusMutation();
  const bulkConfirmModal = useModal();
  const [pendingBulkStatus, setPendingBulkStatus] = useState<LookupStatus | null>(null);

  const requestBulkStatusChange = useCallback((status: LookupStatus) => {
    setPendingBulkStatus(status);
    bulkConfirmModal.open();
  }, [bulkConfirmModal]);

  const handleBulkStatusConfirm = useCallback(async () => {
    if (!pendingBulkStatus) return;
    const ids = Array.from(multiSelect.selectedIds) as number[];
    try {
      await bulkChangeStatus({ ids, status: pendingBulkStatus }).unwrap();
      multiSelect.clearSelection();
      bulkConfirmModal.close();
      setPendingBulkStatus(null);
    } catch (err) {
      console.error('Bulk status update failed:', err);
    }
  }, [pendingBulkStatus, multiSelect.selectedIds, bulkChangeStatus, multiSelect.clearSelection, bulkConfirmModal]);

  const handleBulkConfirmClose = useCallback(() => {
    bulkConfirmModal.close();
    setPendingBulkStatus(null);
  }, [bulkConfirmModal]);

  const bulkActions: BulkAction[] = useMemo(() => {
    if (multiSelect.selectedCount === 0) return [];
    return [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: RefreshCw,
        items: LOOKUP_STATUS_OPTIONS.map((opt) => ({
          key: opt.value,
          label: opt.label,
          onClick: () => requestBulkStatusChange(opt.value as LookupStatus),
        })),
      },
    ];
  }, [multiSelect.selectedCount, requestBulkStatusChange]);

  // Memoize table columns to prevent recreation - only recreate when handlers or groups change
  const tableColumns = useMemo(() => 
    createConditionsColumns(conditionGroupsMap), 
    [conditionGroupsMap]
  );

  // Memoize table actions
  const tableActions = useMemo(() => 
    createConditionsActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize filter change handlers to prevent recreating on every render
  const handleStatusFilterChange = useCallback(
    (value: string) => handleFilterChange("status", value),
    [handleFilterChange]
  );
  
  const handleSpecialityFilterChange = useCallback(
    (value: string) => handleFilterChange("speciality_id", value),
    [handleFilterChange]
  );

  // Memoize specialities options to prevent recreating array on every render
  const specialityOptions = useMemo(
    () => [
      { value: "all", label: "All Specialities" },
      ...specialities.map(speciality => ({
        value: speciality.id.toString(),
        label: speciality.title,
      })),
    ],
    [specialities]
  );

  // Memoize search filters configuration
  const searchFilters: Filter[] = useMemo(
    () => [
      {
        key: "status",
        label: "All Status",
        value: filters.status,
        options: LOOKUP_STATUS_FILTER_OPTIONS,
        onChange: handleStatusFilterChange,
      },
      {
        key: "speciality_id",
        label: "All Specialities",
        value: filters.speciality_id,
        options: specialityOptions,
        onChange: handleSpecialityFilterChange,
      },
    ],
    [filters.status, filters.speciality_id, handleStatusFilterChange, handleSpecialityFilterChange, specialityOptions]
  );

  // Memoize page change handler
  const handlePageChange = useCallback((page: number) => {
    handleFilterChange('page', page.toString());
  }, [handleFilterChange]);

  // Memoize page size change handler
  const handlePageSizeChange = useCallback((pageSize: number) => {
    handleFilterChange('limit', pageSize.toString());
  }, [handleFilterChange]);

  // Memoize modal success handler
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Stable multiSelect configuration object
  const multiSelectConfig = useMemo(() => ({
    selectedIds: multiSelect.selectedIds,
    onToggle: multiSelect.toggle,
    onToggleAll: multiSelect.toggleAll,
    isAllSelected: multiSelect.isAllSelected,
    getItemId: getConditionId,
  }), [multiSelect.selectedIds, multiSelect.toggle, multiSelect.toggleAll, multiSelect.isAllSelected]);

  if (isLoading && !conditionsArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load conditions"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto">
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Seed Conditions"
          subtitle={`${filteredConditions.length} conditions (${paginatedResponse?.total || 0} total)`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Add Condition"
          bulkActions={bulkActions}
          selectedCount={multiSelect.selectedCount}
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search conditions by name or description..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection<ConditionLookup>
          data={filteredConditions}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: ClipboardList,
            title: "No conditions found",
            description: "Create your first condition to get started",
          }}
          multiSelect={multiSelectConfig}
        />

        {paginatedResponse && (
          <Pagination
            paginationInfo={{
              current_page: paginatedResponse.current_page,
              last_page: paginatedResponse.last_page,
              per_page: paginatedResponse.per_page,
              total: paginatedResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            isLoading={isFetching}
          />
        )}

        <ConditionDetailsModal />

        <ConditionCreateModal
          onSuccess={handleModalSuccess}
        />

        <ConditionEditModal
          onSuccess={handleModalSuccess}
        />

        <ConditionGroupsModal onSuccess={refetch} />

        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {bulkConfirmModal.isOpen && pendingBulkStatus && (
          <ConfirmationModal
            isOpen={bulkConfirmModal.isOpen}
            onClose={handleBulkConfirmClose}
            onConfirm={handleBulkStatusConfirm}
            title="Confirm Bulk Status Change"
            message={`Are you sure you want to change ${multiSelect.selectedCount} condition${multiSelect.selectedCount > 1 ? 's' : ''} to "${getStatusBadgeProps(pendingBulkStatus).label}"?`}
            confirmText="Change Status"
            isLoading={isBulkUpdating}
            type="warning"
          />
        )}
      </VStack>
    </Box>
  );
};

export default ConditionsPage;
